/*
  product.js: buy box behavior. Owner: BUYBOX. ES module, loaded by sections/main-product.liquid.
  Spec: design/DESIGN-SSC.md 6.3 to 6.15 and 7; CONTRACT.md E3.4 (events), E3.8 (code maths).
  <bb-product>  selection, code-aware pack prices, add-on, Add to cart (label + live total), express button
                visibility, price-chip .is-tight, sticky bar, buybox:change / buybox:submit.
  Stage behavior (counter, thumbnails, founder chip, founder video) lives in assets/bb-media.js, imported
  on the first interaction with the stage.
  Listens: code:applied (replay), code:removed, buybox:submit, cart:opened, cart:closed.
  Emits:   buybox:change on init and every change, cart:open after a successful add.
  Adds only through cart.add() (one /cart/add.js request, add-on included). Never touches the URL.
  Code maths: codePrice(cents, pct) = cents - floor(cents * pct / 100) (Shopify's rounding, matches /cart.js:
  $52.50, $93.75, $127.50). Per case = round(total / n). Save = struck - shown.
  Budget: <= 4.5 KB minified. Written as closures so the minifier can shorten names.
*/
import { bus, codes, codePrice, formatMoney as money } from '@theme/global';

const doc = document;
const all = (s, r) => [...r.querySelectorAll(s)];
const fill = (s, m) => String(s || '').replace(/\[(\w+)\]/g, (_, k) => (k in m ? m[k] : ''));
const desk = matchMedia('(min-width: 1080px)');
const offset = (px) => doc.documentElement.style.setProperty('--sticky-offset', px + 'px');

function boot(root) {
  const $ = (k, r = root) => r && r.querySelector(`[data-bb=${k}]`);
  let d;
  try {
    d = JSON.parse($('json').textContent);
  } catch (e) {
    return;
  }
  const t = d.t;
  const ac = new AbortController();
  const on = (el, ev, fn) => el && el.addEventListener(ev, fn, { signal: ac.signal, capture: true });
  const sub = (ev, fn, replay) => bus.on(ev, fn, { replay, signal: ac.signal });
  const form = doc.getElementById(root.dataset.form);
  const idIn = $('id');
  const qty = $('qty');
  const btn = $('atc');
  const atcl = $('atcl');
  const atcp = $('atcp');
  const err = $('err');
  const dyn = $('dyn');
  const note = $('note');
  const head = $('packhead');
  const addon = $('addon');
  const check = $('check');
  const sizes = $('sizes');
  const bar = $('sticky');
  const sbtn = $('sbtn');
  const gal = root.querySelector('bb-gallery');
  const thumb = gal && gal.dataset.thumb;
  const lab = $('slabel');
  const lab0 = lab && lab.textContent;
  const radios = all('[data-pack]', root);
  const rows = all('[data-for]', root);
  const vis = new Map();
  let code = null;
  let singles = false;
  let sel = null;
  let open = 0;
  let io;

  /* One purchasable option. Case products: k = '1'|'2'|'3' (the single, 2-pack, 3-pack products). Only if a
     code covers the single but no pack and code_pack_mode = singles (spec 7 fallback gate), or a pack product
     is missing, do 2 and 3 become 2 or 3 single cases. Other products: k = 'v0', 'v1', ... */
  const opt = (k) => {
    const n = d.case ? +k : 1;
    const many = d.case && n > 1 && (singles || !d.opts[k]);
    const o = many ? d.opts[1] : d.opts[k];
    if (!o) return null;
    const q = many ? n : 1;
    const coded = !!code && code.h.includes(o.h);
    const now = (coded ? codePrice(o.price, code.pct) : o.price) * q;
    return { n, q, id: o.id, h: o.h, ok: o.ok, coded, now, was: Math.max(o.cmp || 0, o.price) * q };
  };

  const set = (el, k, v, show) => {
    const x = $(k, el);
    if (!x) return;
    if (v != null) x.textContent = v;
    if (show != null) x.hidden = !show;
  };
  /* Pack card or price row from an option. */
  const paint = (el, o, card) => {
    const save = o.was - o.now;
    const txt = save > 0 ? fill(t.save, { amount: money(save) }) : '';
    set(el, 'now', money(o.now));
    set(el, 'was', money(o.was));
    set(el, 'wasbox', null, save > 0);
    if (card) {
      set(el, 'each', o.n > 1 ? fill(t.each, { price: money(Math.round(o.now / o.n)) }) : t.one);
      set(el, 'save', o.ok ? txt : t.sold);
    } else {
      set(el, 'chip', null, save > 0);
      set(el, 'ccode', o.coded ? code.c + ' · ' : '');
      set(el, 'csave', txt);
    }
  };

  /* null = not ticked, false = ticked without a size, else { id, size }. */
  const addonSel = () => {
    if (!check || !check.checked) return null;
    if (!sizes) {
      const h = $('size0');
      return h ? { id: +h.value, size: '' } : null;
    }
    const r = sizes.querySelector('input:checked');
    return r ? { id: +r.value, size: r.dataset.label } : false;
  };

  const sizeError = (show) => {
    const e = $('sizeerr');
    if (!sizes || !e) return;
    sizes.setAttribute('aria-invalid', show);
    e.textContent = show ? e.dataset.msg : '';
  };

  /* .is-tight hides the "[CODE] · " part of the chip when the price row would overflow. */
  const tight = () =>
    rows.forEach((row) => {
      row.classList.remove('is-tight');
      if (row.scrollWidth > row.clientWidth + 1) row.classList.add('is-tight');
    });

  const render = () => {
    const one = d.opts[1];
    singles = d.case && !!code && codes.packMode == 'singles' && !!one && code.h.includes(one.h) && !code.h.includes((d.opts[2] || {}).h);
    const r = radios.find((x) => x.checked && !x.disabled) || radios.find((x) => !x.disabled);
    if (r) r.checked = true;
    const k = r ? r.dataset.pack : d.case ? '1' : 'v0';
    if (d.case)
      radios.forEach((x) => {
        const o = opt(x.dataset.pack);
        if (o) paint(x.closest('label'), o, 1);
      });
    const o = (sel = opt(k));
    if (!o) return;
    rows.forEach((row) => {
      const f = d.case ? row.dataset.for : k;
      const ro = opt(f);
      if (ro) paint(row, ro);
      row.classList.toggle('is-on', f == k);
    });
    all('label', root).forEach((l) => {
      const i = l.querySelector('input');
      if (i) l.classList.toggle('is-selected', i.checked);
    });
    const a = addonSel();
    const ticked = !!(check && check.checked);
    const ap = ticked ? +addon.dataset.price || 0 : 0;
    const total = o.now + ap;
    const label =
      (d.case ? t.packs[o.n - 1] : lab0 + (r && r.dataset.label ? ' · ' + r.dataset.label : '')) +
      (ticked ? ' ' + fill(t.plus, { size: (a && a.size) || '' }).replace(' ()', '') : '') +
      (o.coded ? ' · ' + code.c : '');
    if (addon) addon.classList.toggle('is-selected', ticked);
    if (idIn) idIn.value = o.id;
    if (qty) qty.value = o.q;
    if (btn) btn.disabled = !o.ok;
    if (atcl) atcl.textContent = o.ok ? btn.dataset[ticked ? 'labelBoth' : 'label'] : t.sold;
    if (atcp) atcp.textContent = o.ok ? money(total) : '';
    if (note) note.textContent = o.coded ? fill(t.note, { code: code.c, percent: code.pct }) : '';
    if (head) head.textContent = code ? fill(t.head, { code: code.c }) : head.dataset.default;
    /* Express buys the form's id (pack variants included, CONTRACT R21), never the add-on. */
    if (dyn) dyn.hidden = ticked;
    if (bar) {
      set(bar, 'sprice', money(total));
      set(bar, 'swas', o.was > o.now ? money(o.was + (ticked ? +addon.dataset.full || 0 : 0)) : '');
      set(bar, 'slabel', label);
      if (sbtn) sbtn.disabled = !o.ok;
    }
    tight();
    bus.emit('buybox:change', {
      handle: o.h,
      variantId: o.id,
      quantity: o.q,
      packSize: o.n,
      mode: singles ? 'singles' : 'packs',
      price: o.now,
      compareAt: o.was > o.now ? o.was : null,
      codePrice: o.coded ? o.now : null,
      code: o.coded ? code.c : null,
      pct: o.coded ? code.pct : null,
      addon: ticked ? { variantId: a ? a.id : null, size: a ? a.size : null, price: ap } : null,
      total,
      label,
      thumb,
    });
  };

  /* Submit path (validation, one cart.add(), busy state, errors, cart:open) lives in assets/bb-add.js: imported
     on the first submit and prefetched once the page has loaded. */
  let mod;
  const lazy = () => (mod = mod || import(root.dataset.addJs));
  const ctx = { sel: () => sel, addonSel, sizeError, sizes, err, btn, sbtn, t };
  const add = (src, trig) => lazy().then((m) => m.add(ctx, src, trig));
  addEventListener('load', () => setTimeout(lazy, 1500), { once: true });

  /* Sticky bar: visible whenever the main Add to cart and every [data-sticky-sentinel] (final CTA, footer)
     are off-screen and the drawer is closed; never from 1080px. */
  const stick = () => {
    if (!bar) return;
    const show = !desk.matches && !open && ![...vis.values()].some(Boolean);
    if (show == bar.classList.contains('is-visible')) return;
    bar.classList.toggle('is-visible', show);
    offset(show ? bar.offsetHeight : 0);
  };

  /* From here on JS owns the variant id: the radios stop posting, the hidden input in the form posts. */
  radios.forEach((r) => {
    r.name = 'bb-pack-' + root.dataset.form;
    r.removeAttribute('form');
  });
  if (idIn) idIn.name = 'id';
  if (thumb && $('sthumb')) $('sthumb').src = thumb;
  if (root.querySelector('[data-trust-pos]')) all('.bb-trust-after', root).forEach((x) => (x.hidden = true));

  on(root, 'change', (e) => {
    const el = e.target;
    if (el == check && sizes) sizes.hidden = !el.checked;
    if (el == check || (sizes && sizes.contains(el))) sizeError(false);
    render();
  });
  on(form, 'submit', (e) => {
    e.preventDefault();
    add('buybox');
  });
  on(sbtn, 'click', () => add('sticky'));
  sub('buybox:submit', (x) => add((x && x.source) || 'content', x && x.trigger));
  sub(
    'code:applied',
    (x) => {
      const rule = codes.rule(x && x.code) || {};
      code = x && x.pct > 0 ? { c: x.code, pct: +x.pct, h: x.appliesToHandles || rule.handles || [] } : null;
      render();
    },
    1
  );
  sub('code:removed', () => render((code = null)));
  sub('cart:opened', () => stick((open = 1)));
  sub('cart:closed', () => stick((open = 0)));
  let raf;
  on(window, 'resize', () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(tight);
  });
  if (doc.fonts) doc.fonts.ready.then(tight);

  /* Stage behavior (counter, thumbnails, founder chip, video) loads on the first touch, scroll, focus or click
     in the stage: assets/bb-media.js. */
  if (gal && gal.dataset.mediaJs) {
    let p;
    const ld = () => (p = p || import(gal.dataset.mediaJs).then((m) => m.init(gal)));
    ['click', 'scroll', 'pointerdown', 'focusin'].forEach((ev) => on(gal, ev, (e) => ld().then((h) => h(e), () => {})));
  }
  if (bar && window.IntersectionObserver) {
    io = new IntersectionObserver((es) => {
      es.forEach((e) => vis.set(e.target, e.isIntersecting));
      stick();
    });
    [btn, ...all('[data-sticky-sentinel],.site-footer', doc)].forEach((x) => x && io.observe(x));
    on(desk, 'change', stick);
  }
  render();

  return () => {
    ac.abort();
    if (io) io.disconnect();
    offset(0);
  };
}

class BBProduct extends HTMLElement {
  connectedCallback() {
    if (!this.stop) this.stop = boot(this);
  }
  disconnectedCallback() {
    if (this.stop) this.stop();
    this.stop = null;
  }
}

if (!customElements.get('bb-product')) customElements.define('bb-product', BBProduct);
